<template>
  <div>
    <div class="section1 safe-area">
      <pageTitle cn="华夏非遗探究" en="Exploring China's Intangible Cultural Heritage" type="1"></pageTitle>
      <div class="page-flex-lr-center-between">
        <div class="lf">
          <div class="common-title-d1 mt-20">在华夏九百六十余万平方公里的土地上，三秦、燕赵、齐鲁、吴越、三晋、巴蜀、荆楚、岭南、东北九大文化片区如同九颗璀璨的文化明珠，各自承载着独属于一方水土的非遗记忆，串联起跨越数千年的中华文脉脉络。从黄土高原的秦腔古乐到江南水乡的苏绣评弹，从川蜀盆地的变脸灯彩到南海之滨的粤韵侨风，不同地域的非遗技艺带着各自的风土烙印与民族温度，共同铺展开一幅活态传承的华夏文明长卷。</div>
          <div class="common-title-d2 mt-12">Across the vast land of China spanning over 9.6 million square kilometers, nine major cultural regions — Sanqin, Yanzhao, Qilu, Wuyue, Sanjin, Bashu, Jingchu, Lingnan and Northeast China — shine as nine brilliant cultural pearls. Each carries the intangible cultural heritage memories exclusive to its local land, linking together the millennia-long historical context of Chinese civilization. From the ancient Qin Opera and classical music on the Loess Plateau to Suzhou Embroidery and Pingtan ballads in the misty Jiangnan water towns, from the face-changing stunts and dazzling lantern fairs in Sichuan basins to the Cantonese melodies and overseas Chinese cultural customs along the South China coast, intangible cultural heritage crafts from different regions, marked with local customs and ethnic warmth, jointly unfold a living scroll of Chinese civilization that has been passed down through generations.</div>
          <img class="mt-20" :src="$env.imgUrl + 'explore2-s1-2.png'" alt="">
          <div class="common-title-d1">我们甄选国內五大文化区域，筛选了一系列致力于保护和传承非物质文化遗产的博物馆、民俗馆等作为参观研学的基地，特邀非遗传承保护人进行实地讲解和展演，让学生感受他们对非遗事业的热爱，体会非遗的魅力。</div>
        </div>
        <div class="rt">
          <img :src="$env.imgUrl + 'explore2-s1-1.png'" alt="">
        </div>
      </div>
    </div>
    <div class="section2" :style="'background-image: url(' + $env.imgUrl + 'pbl-bg-1.png);'">
      <div class="safe-area">
        <pageTitle cn="全球非遗探究" en="Exploring Intangible Cultural Heritage Around the World" type="2"></pageTitle>
        <div class="common-title-d3 mt-20">大会每年都会精选来自五大洲的不同国家作为出访目的地。小使者们可以走出国门，去欧洲探寻文艺复兴的起源、去亚洲感受文化的灿烂多元、去北美洲了解兼收并蓄的气度、去南美洲感觉热情奔放的狂热、去非洲观察原始质朴的生机。</div>
        <div class="common-title-d4 mt-12">Each year, the conference selects different countries from all five continents as destinations for its study tours. The young ambassadors have the opportunity to travel abroad—to Europe to explore the origins of the Renaissance, to Asia to experience its rich and diverse culture, to North America to learn about its inclusive spirit, to South America to feel its passionate and exuberant energy, and to Africa to observe its unspoiled and authentic vitality.</div>
        <div class="page-flex-lr-center-between">
          <img :src="$env.imgUrl + 'explore2-s2-1.png'" alt="">
          <img :src="$env.imgUrl + 'explore2-s2-2.png'" alt="">
          <img :src="$env.imgUrl + 'explore2-s2-3.png'" alt="">
          <img :src="$env.imgUrl + 'explore2-s2-4.png'" alt="">
          <img :src="$env.imgUrl + 'explore2-s2-5.png'" alt="">
        </div>
      </div>
    </div>
    <div class="section3">
      <div class="safe-area">
        <div class="tit-cn">四年来，小使者走遍了两大洲、五个国家、十个城市，用脚步丈量了25045公里的世界。</div>
        <div class="tit-en">Over the past four years, the little messenger has traveled to two continents, five countries, and ten cities, covering a distance of 25,045 kilometers around the world.</div>
        <img class="mt-20" :src="$env.imgUrl + 'explore2-s3.png'" alt="">
        <div class="common-title-d1 mt-20">此外，为保证学有所长，研学途中，组委会精心打造 “小使者手账书” ，以手绘记录出访足迹，让中国与泰国、马来西亚、芬兰、丹麦、日本等国的文明相遇凝结于笔尖，一页一篇，珍藏成长。国家文旅部主管的 《中外文化交流杂志》 更开辟小使者专栏，特邀他们以独特的亲身视角，书写旅途中所见所闻、所感所思，让世界听见中国少年的声音。</div>
        <div class="common-title-d2 mt-12">In addition, to ensure that the students learn something valuable, the organizing committee carefully created a "Little Messenger Handbook" during the study tour, which records their visits with hand-drawn illustrations, allowing the encounter between Chinese and foreign civilizations, such as those from Thailand, Malaysia, Finland, Denmark, and Japan, to be condensed at the tip of the pen. Each page represents a chapter, cherishing the growth of the students. The "Sino-Foreign Cultural Exchange Magazine" under the Ministry of Culture and Tourism has also opened a special column for the Little Messengers, inviting them to write about what they see, hear, feel, and think during the journey from their unique personal perspectives, allowing the world to hear the voices of Chinese teenagers.</div>
      </div>
    </div>
    <div class="section4" :style="'background-image: url(' + $env.imgUrl + 'explore-bg.png);'">
      <div class="safe-area">
        <pageTitle cn="往期使者访谈" en="Previous Messenger Interviews" type="2"></pageTitle>
        <div class="page-flex-lr-center-between mt-20">
          <img :src="$env.imgUrl + 'explore2-s4-1.png'" alt="">
          <img :src="$env.imgUrl + 'explore2-s4-2.png'" alt="">
        </div>
        <div class="page-flex-lr-center-between mt-20">
          <img :src="$env.imgUrl + 'explore2-s4-3.png'" alt="">
          <img :src="$env.imgUrl + 'explore2-s4-4.png'" alt="">
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  data () {
    return {
    }
  }
}
</script>
<style scoped lang="less">
.mt-20{
  margin-top: 20px;
}
.section1{
  padding: 30px 0 40px;
  .lf{
    width: 45%;
  }
  .rt{
    flex: 1;
    margin-left: 20px;
    img{
      width: 100%;
    }
  }
}
.section2{
  padding: 80px 0;
  margin-bottom: 10px;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  img{
    width: 19%;
  }
}
.section3 {
  padding: 20px 0 60px;
  .s3-item{
    width: 49%;
    text-align: center;
    .cn{
      margin-top: 20px;
      font-family: systh;
      font-size: 16px;
      color: #000;
    }
    .en{
      margin-top: 4px;
      font-family: systh;
      font-size: 10px;
      color: #000;
      margin-bottom: 20px;
    }
    img{
      width: 100%;
      height: auto;
    }
  }
}
.section4{
  padding: 80px 0;
  background-repeat: no-repeat;
  background-size: 100% auto;
  img{
    width: 49%;
  }
}

.tit-cn{
  font-family: systh;
  font-size: 18px;
  color: #000;
}
.tit-en{
  font-family: systm;
  font-size: 10px;
  color: #999;
  margin-bottom: 20px;
}

</style>
